// @flow
/* eslint react/no-did-mount-set-state:0 */

import * as React from 'react';
import * as StringUtil from 'app/common/lib/StringUtil';
import i18n from 'app/common/lib/i18n';
import type { BasicImage } from 'app/packages/pinterest-flow-types/image';
import { Box, IconButton, Spinner } from 'gestalt';

export function BoardCoverImageCropPlaceholder() {
  return (
    <div className="ImageCrop">
      <div className="holder">
        <Box
          position="absolute"
          top
          bottom
          left
          right
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Spinner
            accessibilityLabel={i18n._('Loading', 'accessibility label for loading spinner')}
            show
          />
        </Box>
      </div>
    </div>
  );
}

const THUMB_KEY = '236x';
const ARROW_SIZE = 46;

export type BoardCoverImageCropData = {
  cropX: number,
  cropY: number,
  height: number,
  width: number,
  scale: number,
};

type Props = {|
  boardName: string,
  frameHeight: number,
  frameWidth: number,
  isFirst: boolean,
  isLast: boolean,
  pin: {
    id: string,
    images: {
      '236x': BasicImage,
    },
  },
  showNextPin: () => void,
  showPrevPin: () => void,
  updateImgCrop: (cropData: BoardCoverImageCropData) => void,
|};

type State = {|
  deltaY: number,
  deltaX: number,
  holderHeight: number,
  holderWidth: number,
  isSpinnerShown: boolean,
  startBgPos: {
    left: number,
    top: number,
  },
|};

const STYLES = {
  arrowButton: {
    border: 0,
    top: 120,
  },
};

export default class BoardCoverImageCrop extends React.Component<Props, State> {
  static defaultProps = {
    frameHeight: 236,
    frameWidth: 236,
  };

  state = {
    deltaY: 0,
    deltaX: 0,
    holderHeight: 0,
    holderWidth: 0,
    isSpinnerShown: true,
    startBgPos: {
      left: 0,
      top: 0,
    },
  };

  componentDidMount() {
    if (this.holder) {
      this.setState({
        holderHeight: this.holder.offsetHeight,
        holderWidth: this.holder.offsetWidth,
      });
    }
    if (document.body) {
      document.body.addEventListener('mouseup', this.handleOnMouseUp, false);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.pin.id !== this.props.pin.id) {
      this.setState({ isSpinnerShown: true });
    }
  }

  componentWillUnmount() {
    if (document.body) {
      document.body.removeEventListener('mouseup', this.handleOnMouseUp);
    }
  }

  getFrameBounds() {
    const { frameHeight, frameWidth } = this.props;
    const { holderHeight, holderWidth } = this.state;

    return {
      left: holderWidth / 2 - frameWidth / 2,
      width: frameWidth,
      height: frameHeight,
      top: holderHeight / 2 - frameHeight / 2,
    };
  }

  getBgLeft() {
    const { frameWidth } = this.props;
    const frameBounds = this.getFrameBounds();
    const imgScale = this.getImgScale();
    const imgDim = this._getImageDimensions();
    const imgHeight = imgDim.imgHeight * imgScale;
    const imgWidth = imgDim.imgWidth * imgScale;

    let left = 0;

    if (!!imgHeight && !!imgWidth) {
      left = this.state.startBgPos.left + this.state.deltaX;

      if (left + imgWidth - frameWidth < frameBounds.left) {
        left = frameBounds.left - imgWidth + frameWidth;
      }
      if (left > frameBounds.left) {
        left = frameBounds.left;
      }
    }

    return left;
  }

  /**
   * Looks at the previous bgTop and accounts for the current deltaY. Sets the
   * appropriate new bgTop, factoring in the frame bounds.
   */
  getBgTop() {
    const { frameHeight } = this.props;
    const frameBounds = this.getFrameBounds();
    const imgScale = this.getImgScale();
    const imgDim = this._getImageDimensions();
    const imgHeight = imgDim.imgHeight * imgScale;
    const imgWidth = imgDim.imgWidth * imgScale;

    let top = 0;

    if (!!imgHeight && !!imgWidth) {
      top = this.state.startBgPos.top + this.state.deltaY;

      if (top + imgHeight - frameHeight < frameBounds.top) {
        top = frameBounds.top - imgHeight + frameHeight;
      }

      if (top > frameBounds.top) {
        top = frameBounds.top;
      }
    }

    return top;
  }

  getImgScale() {
    const { frameHeight, frameWidth } = this.props;
    const { imgHeight, imgWidth } = this._getImageDimensions();

    const frameBounds = this.getFrameBounds();

    if (!!imgHeight && !!imgWidth) {
      let imageRatio = imgHeight / imgWidth;

      const frameRatio = frameBounds.height / frameBounds.width;

      if (imageRatio >= frameRatio) {
        imageRatio = frameWidth / imgWidth;
      } else {
        imageRatio = frameHeight / imgHeight;
      }

      return imageRatio;
    }

    return 1;
  }

  getImageBounds() {
    const { imgHeight, imgWidth } = this._getImageDimensions();
    let imageBounds = {};

    if (!!imgHeight && !!imgWidth) {
      const imageRatio = this.getImgScale();

      imageBounds = {
        width: imageRatio * imgWidth,
        height: imageRatio * imgHeight,
      };
    }

    return imageBounds;
  }

  getBgBounds() {
    return {
      left: this.getBgLeft(),
      top: this.getBgTop(),
    };
  }

  getCropData() {
    const { frameHeight, frameWidth } = this.props;
    const frameBounds = this.getFrameBounds();
    const bgBounds = this.getBgBounds();
    const scale = this.getImgScale();

    return {
      cropX: Math.round(-(bgBounds.left - frameBounds.left) / scale),
      cropY: Math.round(-(bgBounds.top - frameBounds.top) / scale),
      height: Math.round(frameHeight / scale),
      width: Math.round(frameWidth / scale),
      scale,
    };
  }

  handleUpdateImage = () => {
    const image = this.image;
    if (!image) {
      return;
    }
    const offsetHeight = image.offsetHeight;
    const offsetWidth = image.offsetWidth;

    // should only update state if the image's natural width and height from
    // pin tuple is wrong.

    this.setState({
      isSpinnerShown: false,
      startBgPos: {
        left: -(offsetWidth - this.state.holderWidth) / 2,
        top: -(offsetHeight - this.state.holderHeight) / 2,
      },
    });
  };

  handleOnMouseDown(e: SyntheticMouseEvent<HTMLElement>) {
    if (e.button === 0) {
      e.stopPropagation();

      this.isDragging = true;
      this.originX = e.pageX;
      this.originY = e.pageY;
    }
  }

  handleOnMouseMove(e: SyntheticMouseEvent<HTMLElement>) {
    if (this.isDragging) {
      this.setState({
        deltaX: e.pageX - (this.originX || 0),
        deltaY: e.pageY - (this.originY || 0),
      });
    }
  }

  handleOnMouseUp = () => {
    this.isDragging = false;
    this.setState({
      startBgPos: {
        left: this.getBgLeft(),
        top: this.getBgTop(),
      },
      deltaX: 0,
      deltaY: 0,
    });

    this.props.updateImgCrop(this.getCropData());
  };

  // Helper function to return an object of the image's width and height.
  _getImageDimensions() {
    const { pin } = this.props;
    const image = pin.images[THUMB_KEY];

    return { imgHeight: image.height, imgWidth: image.width };
  }

  holder: ?HTMLElement;
  image: ?HTMLImageElement;
  isDragging: boolean = false;
  originX: ?number;
  originY: ?number;

  render() {
    const {
      boardName,
      frameHeight,
      frameWidth,
      isFirst,
      isLast,
      pin,
      showNextPin,
      showPrevPin,
    } = this.props;
    const { isSpinnerShown } = this.state;
    const { imgHeight, imgWidth } = this._getImageDimensions();

    // some positioning math happening here
    const frameBounds = this.getFrameBounds();
    const imageBounds = this.getImageBounds();
    const bgBounds = this.getBgBounds();

    let imageClass = '';
    if (!!imgHeight && !!imgWidth) {
      imageClass = 'in';
    }

    return (
      <div className="ImageCrop">
        <div
          className="holder"
          ref={(holder) => {
            this.holder = holder;
          }}
        >
          <div className="bg" style={bgBounds}>
            <img
              alt={StringUtil.dangerouslyFormat(i18n._('Board cover for {{ boardName }}'), {
                boardName,
              })}
              className={imageClass}
              onLoad={this.handleUpdateImage}
              ref={(image) => {
                this.image = image;
              }}
              src={pin.images['236x'].url}
              style={imageBounds}
            />
          </div>
          <div className="mask">
            <span style={{ top: 0, left: 0, right: 0, height: frameBounds.top }} />
            <span
              style={{
                top: frameBounds.top,
                left: frameBounds.left + frameWidth,
                right: 0,
                height: frameHeight,
              }}
            />
            <span style={{ top: frameHeight + frameBounds.top, left: 0, right: 0, bottom: 0 }} />
            <span
              style={{
                top: frameBounds.top,
                left: 0,
                width: frameBounds.left,
                height: frameHeight,
              }}
            />
          </div>
          <div className="frame" style={frameBounds} />
          {/* eslint-disable jsx-a11y/no-static-element-interactions */}
          <div
            className="drag"
            onMouseDown={(e) => {
              this.handleOnMouseDown(e);
            }}
            onMouseMove={(e) => {
              this.handleOnMouseMove(e);
            }}
          />
          {/* eslint-enable jsx-a11y/no-static-element-interactions */}
          <div className="spinner" style={{ display: isSpinnerShown ? '' : 'none' }} />
        </div>
        {isFirst ? null : (
          <Box
            position="absolute"
            width={ARROW_SIZE}
            height={ARROW_SIZE}
            dangerouslySetInlineStyle={{ __style: { ...STYLES.arrowButton, left: 100 } }}
          >
            <IconButton
              icon="arrow-back"
              accessibilityLabel={i18n._(
                'Previous',
                'Label for indicating selecting the previous pin',
              )}
              onClick={showPrevPin}
            />
          </Box>
        )}
        {isLast ? null : (
          <Box
            position="absolute"
            width={ARROW_SIZE}
            height={ARROW_SIZE}
            dangerouslySetInlineStyle={{ __style: { ...STYLES.arrowButton, right: 100 } }}
          >
            <IconButton
              icon="arrow-forward"
              accessibilityLabel={i18n._('Next', 'Label for indicating selecting the previous pin')}
              onClick={showNextPin}
            />
          </Box>
        )}
      </div>
    );
  }
}
